// @spec SCF-004
// @spec SCF-008
// @spec SHL-STR-005
// @spec AI-STR-016
// eslint-disable-next-line no-restricted-imports -- reads `organization.name` for display only; ID resolution uses `useOrganizationId`.
import { useOrganization, useUser } from '@clerk/clerk-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  BarChart3,
  CheckSquare,
  ChevronRight,
  Cloud,
  Database,
  Shield,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { MetricCard } from '@/features/analytics/components/MetricCard';
import { useGHGDashboard } from '@/features/ghg/api/use-ghg-dashboard';
import { useNIS2Dashboard } from '@/features/nis2/api/dashboard';
import { useMyTaskStats } from '@/features/tasks/api/use-my-task-stats';
import { useOrganizationId } from '@/hooks/use-organization-id';
import { formatNumeric } from '@/lib/format';
import i18n from '@/lib/i18n';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/dashboard')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.home'),
  }),
  component: DashboardPage,
});

function getGreetingKey(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'greeting.morning';
  if (hour < 18) return 'greeting.afternoon';
  return 'greeting.evening';
}

function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { user } = useUser();
  const { organization } = useOrganization();
  const { data: taskStats, isLoading: isTasksLoading } = useMyTaskStats();

  const organizationId = useOrganizationId();

  const {
    totalEmissions,
    emissionsTrend,
    dataQuality,
    isLoading: isGHGLoading,
    includeSubsidiaries: ghgIncludesSubs,
    includedOrganizationCount: ghgOrgCount,
  } = useGHGDashboard();

  // How many subsidiaries contributed to the rolled-up total (root excluded).
  const rolledUpSubCount = ghgIncludesSubs && ghgOrgCount > 1 ? ghgOrgCount - 1 : 0;

  const { data: nis2Data, isLoading: isNIS2Loading } = useNIS2Dashboard({
    orgId: organizationId ?? '',
    enabled: !!organizationId,
  });

  const displayName = user?.fullName ?? user?.firstName ?? 'User';
  const orgName = organization?.name ?? '';
  const openTaskCount =
    (taskStats?.byStatus.not_started ?? 0) +
    (taskStats?.byStatus.in_progress ?? 0) +
    (taskStats?.byStatus.blocked ?? 0) +
    (taskStats?.byStatus.pending_review ?? 0);

  return (
    <div className="bg-background p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-4">
        {/* Section 1: Compact Status Bar */}
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-4 py-2.5">
          <div className="text-sm text-foreground">
            <span className="font-medium">{t(getGreetingKey())}, {displayName}</span>
            <span className="text-muted-foreground"> — {orgName}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {!isTasksLoading && openTaskCount > 0 && (
              <Link
                to="/tasks/my"
                data-testid="dashboard-open-tasks-link"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('statusBar.openTasks', { count: openTaskCount })}
              </Link>
            )}
          </div>
        </div>

        {/* Section 2: KPI Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Link
            to="/ghg/dashboard"
            data-testid="dashboard-kpi-ghg-link"
            className="group block"
          >
            <div className="transition-all duration-200 group-hover:shadow-md group-hover:-translate-y-0.5">
              <MetricCard
                title={t('kpi.ghgEmissions')}
                value={totalEmissions ? formatNumeric(totalEmissions.total, 1) : '--'}
                subtitle={
                  rolledUpSubCount > 0
                    ? t('kpi.ghgSubtitleRolledUp', {
                        defaultValue:
                          '{{base}} · includes {{count}} subsidiaries',
                        base: t('kpi.ghgSubtitle'),
                        count: rolledUpSubCount,
                      })
                    : t('kpi.ghgSubtitle')
                }
                icon={Cloud}
                variant="success"
                trend={emissionsTrend?.trend}
                changePercent={emissionsTrend?.changePercent}
                isLoading={isGHGLoading}
                className="overflow-hidden border-l-4 border-l-emerald-500"
              />
            </div>
          </Link>

          <Link
            to="/nis2/dashboard"
            data-testid="dashboard-kpi-nis2-link"
            className="group block"
          >
            <div className="transition-all duration-200 group-hover:shadow-md group-hover:-translate-y-0.5">
              <MetricCard
                title={t('kpi.nis2Compliance')}
                value={nis2Data?.summary ? `${String(nis2Data.summary.overallComplianceScore)}%` : '--'}
                subtitle={t('kpi.nis2Subtitle')}
                icon={Shield}
                variant="default"
                isLoading={isNIS2Loading}
                className="overflow-hidden border-l-4 border-l-accent-500"
              />
            </div>
          </Link>

          <Link
            to="/tasks/my"
            data-testid="dashboard-kpi-tasks-link"
            className="group block"
          >
            <div className="transition-all duration-200 group-hover:shadow-md group-hover:-translate-y-0.5">
              <MetricCard
                title={t('kpi.openTasks')}
                value={isTasksLoading ? '--' : openTaskCount}
                subtitle={t('kpi.tasksSubtitle')}
                icon={CheckSquare}
                variant="warning"
                isLoading={isTasksLoading}
                className="overflow-hidden border-l-4 border-l-amber-500"
              />
            </div>
          </Link>
        </div>

        {/* Section 3: Compliance Overview + Recent Activity */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('complianceOverview.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('complianceOverview.ghgEmissions')}
                    {rolledUpSubCount > 0 && (
                      <span className="ml-2 text-xs text-emerald-700 dark:text-emerald-400">
                        {t('complianceOverview.includesSubsidiaries', {
                          defaultValue: '(incl. {{count}} subsidiaries)',
                          count: rolledUpSubCount,
                        })}
                      </span>
                    )}
                  </span>
                  <span className="font-medium">
                    {totalEmissions ? formatNumeric(totalEmissions.total, 1) + ' tCO2e' : '--'}
                  </span>
                </div>
                <Progress value={dataQuality?.coveragePercent ?? 0} className="h-2" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('complianceOverview.nis2')}</span>
                  <span className="font-medium">
                    {nis2Data?.summary ? `${String(nis2Data.summary.overallComplianceScore)}%` : '--'}
                  </span>
                </div>
                <Progress value={nis2Data?.summary.overallComplianceScore ?? 0} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('recentActivity.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                {t('recentActivity.noActivity')}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section 4: Quick Actions */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {/* GHG Quick Action — dynamic based on readiness */}
          <Link
            to="/ghg/dashboard"
            data-testid="dashboard-quick-action-ghg"
            className={cn(
              'group flex items-center gap-4 rounded-lg border border-border p-4',
              'transition-colors hover:bg-muted/50'
            )}
          >
            <div className="rounded-lg bg-emerald-500/10 p-2.5">
              <Cloud className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{t('quickActions.ghgDashboard')}</p>
              <p className="text-xs text-muted-foreground truncate">
                {t('quickActions.ghgDashboardDescription')}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          </Link>

          <Link
            to="/datahub/upload"
            data-testid="dashboard-quick-action-import-activity-data"
            className={cn(
              'group flex items-center gap-4 rounded-lg border border-border p-4',
              'transition-colors hover:bg-muted/50'
            )}
          >
            <div className="rounded-lg bg-blue-500/10 p-2.5">
              <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {t('quickActions.importActivityData')}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {t('quickActions.importActivityDataDescription')}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Link>

          <Link
            to="/tasks/my"
            data-testid="dashboard-quick-action-my-tasks"
            className={cn(
              'group flex items-center gap-4 rounded-lg border border-border p-4',
              'transition-colors hover:bg-muted/50'
            )}
          >
            <div className="rounded-lg bg-amber-500/10 p-2.5">
              <CheckSquare className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{t('quickActions.myTasks')}</p>
              <p className="truncate text-xs text-muted-foreground">
                {t('quickActions.myTasksDescription')}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Link>

          <Link
            to="/nis2/dashboard"
            data-testid="dashboard-quick-action-nis2-compliance"
            className={cn(
              'group flex items-center gap-4 rounded-lg border border-border p-4',
              'transition-colors hover:bg-muted/50'
            )}
          >
            <div className="rounded-lg bg-accent-500/10 p-2.5">
              <Shield className="h-5 w-5 text-accent-600 dark:text-accent-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {t('quickActions.nis2Compliance')}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {t('quickActions.nis2ComplianceDescription')}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Link>

          <Link
            to="/analytics"
            data-testid="dashboard-quick-action-analytics"
            className={cn(
              'group flex items-center gap-4 rounded-lg border border-border p-4',
              'transition-colors hover:bg-muted/50'
            )}
          >
            <div className="rounded-lg bg-purple-500/10 p-2.5">
              <BarChart3 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {t('quickActions.analytics')}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {t('quickActions.analyticsDescription')}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Link>
        </div>
      </div>
    </div>
  );
}
